import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import { UpcastWriter } from 'ckeditor5/src/engine.js';
import { Notification } from 'ckeditor5/src/ui.js';
import { FileRepository } from 'ckeditor5/src/upload';
import { ClipboardPipeline } from 'ckeditor5/src/clipboard.js';
import { createFileTypeRegExp, fetchLocalFile, isLocalFile } from './utils.js';
import UploadFileCommand from './uploadfilecommand.js';
import { isSafari } from '@config/browser';

/**
 * The editing part of the file upload feature. It registers the `'uploadFile'` command
 *
 * When an file is uploaded, it fires the {@link ~FileUploadEditing#event:uploadComplete `uploadComplete`} event
 * that allows adding custom attributes to the {@link module:engine/model/element~Element file element}.
 */
export default class FileUploadEditing extends Plugin {
	/**
     * @inheritDoc
     */
    static get requires() {
        return [FileRepository, Notification, ClipboardPipeline];
    }
    static get pluginName() {
        return 'FileUploadEditing';
    }
    /**
     * @inheritDoc
     */
    constructor(editor) {
        super(editor);

        editor.config.define('fileupload', {
            upload: {
                types: ['*']
            }
        });

        this._uploadFileElements = new Map();
    }

	/**
     * @inheritDoc
     */
    init() {
        const editor = this.editor;
        const doc = editor.model.document;
        const schema = editor.model.schema;
        const conversion = editor.conversion;
        const fileRepository = editor.plugins.get(FileRepository);
        const clipboardPipeline = editor.plugins.get('ClipboardPipeline');
        const fileTypes = createFileTypeRegExp(editor.config.get('fileupload.upload.types'));
        const uploadFileCommand = new UploadFileCommand(editor);

        // Register `uploadFile` command.
        editor.commands.add('uploadFile', uploadFileCommand);

        // Setup schema to allow uploadId and uploadStatus for files.
		schema.extend( '$text', {
			allowAttributes: [
				'uploadId',
				'uploadStatus'
			]
		});

        // Register upcast converter for uploadId.
        conversion.for('upcast')
            .attributeToAttribute({
            view: {
                name: 'a',
                key: 'uploadId'
            },
            model: 'uploadId'
        });

        // Handle pasted files.
		// For every file, a new file loader is created and a placeholder files is
		// inserted into the content. Then, those files are uploaded once they appear in the model
		// (see Document#change listener below).
		this.listenTo(editor.editing.view.document, 'clipboardInput', (evt, data) => {
            // Skip if non empty HTML data is included.
            // https://github.com/ckeditor/ckeditor5-upload/issues/68
            if (isHtmlIncluded(data.dataTransfer)) {
                return;
            }
            const files = Array.from(data.dataTransfer.files).filter(file => {
                // See https://github.com/ckeditor/ckeditor5-image/pull/254.
                if (!file) {
                    return false;
                }
                return fileTypes.test(file.type);
            });
            if (!files.length) {
                return;
            }
            evt.stop();
            editor.model.change(writer => {
                // Set selection to paste target.
                if (data.targetRanges) {
                    writer.setSelection(data.targetRanges.map(viewRange => editor.editing.mapper.toModelRange(viewRange)));
                }
                editor.execute('uploadFile', { file: files });
            });
        });

        // Handle HTML pasted with images with base64 or blob sources.
        // For every image file, a new file loader is created and a placeholder image is
        // inserted into the content. Then, those images are uploaded once they appear in the model
        // (see Document#change listener below).
        this.listenTo(clipboardPipeline, 'inputTransformation', (evt, data) => {
            const fetchableFiles = Array.from(editor.editing.view.createRangeIn(data.content))
                .map(value => value.item)
                .filter(viewElement => isLocalFile(viewElement) &&
                !viewElement.getAttribute('uploadProcessed'))
                .map(viewElement => { return { promise: fetchLocalFile(viewElement), fileElement : viewElement }; });
            if (!fetchableFiles.length) {
                return;
            }
            const writer = new UpcastWriter(editor.editing.view.document);
            for (const fetchableFile of fetchableFiles) {
                // Set attribute marking that the image was processed already.
                writer.setAttribute('uploadProcessed', true, fetchableFile.fileElement );
                const loader = fileRepository.createLoader(fetchableFile.promise);
                if (loader) {
                    writer.setAttribute('href', '', fetchableFile.fileElement );
                    writer.setAttribute('uploadId', loader.id, fetchableFile.fileElement );
                }
            }
        });

        // Prevents from the browser redirecting to the dropped file.
        editor.editing.view.document.on('dragover', (evt, data) => {
            data.preventDefault();
        });

        // Upload placeholder files that appeared in the model.
        doc.on('change', () => {
            // Note: Reversing changes to start with insertions and only then handle removals. If it was the other way around,
            // loaders for **all** files that land in the $graveyard would abort while in fact only those that were **not** replaced
            // by other files should be aborted.
            const changes = doc.differ.getChanges({ includeChangesInGraveyard: true }).reverse();
            const insertedFilesIds = new Set();
            for (const entry of changes) {
                if (entry.type == 'insert') { //&& entry.name != '$text'
                    const item = entry.position.nodeAfter;
                    if(item) {
                        const isInsertedInGraveyard = entry.position.root.rootName == '$graveyard';
                        for (const linkTextElement of getFileLinksFromChangeItem(editor, item)) {
                            // Check if the image element still has upload id.
                            const uploadId = linkTextElement.getAttribute('uploadId');
                            if (!uploadId) {
                                continue;
                            }
                            // Check if the image is loaded on this client.
                            const loader = fileRepository.loaders.get(uploadId);
                            if (!loader) {
                                continue;
                            }
                            if (isInsertedInGraveyard) {
                                // If the image was inserted to the graveyard for good (**not** replaced by another image),
                                // only then abort the loading process.
                                if (!insertedFilesIds.has(uploadId)) {
                                    loader.abort();
                                }
                            } else {
                                // Remember the upload id of the inserted image. If it acted as a replacement for another
                                // image (which landed in the $graveyard), the related loader will not be aborted because
                                // this is still the same image upload.
                                insertedFilesIds.add(uploadId);
                                // Keep the mapping between the upload ID and the image model element so the upload
                                // can later resolve in the context of the correct model element. The model element could
                                // change for the same upload if one image was replaced by another (e.g. image type was changed),
                                // so this may also replace an existing mapping.
                                this._uploadFileElements.set(uploadId, linkTextElement);
                                if (loader.status == 'idle') {
                                    // If the image was inserted into content and has not been loaded yet, start loading it.
                                    this._readAndUpload(loader);
                                }
                            }
                        }
                    }
                }
            }
        });

        this.on('uploadComplete', (evt, { fileElement, data }) => {
        }, { priority: 'low' });
    }

    /**
     * @inheritDoc
     */
    afterInit() {
    }

    /**
     * Reads and uploads an file.
     *
     * The file is read from the disk and as a Base64-encoded string it is set temporarily to
     * `file[src]`. When the file is successfully uploaded, the temporary data is replaced with the target
     * file's URL (the URL to the uploaded file on the server).
     */
    _readAndUpload(loader) {
        const editor = this.editor;
        const model = editor.model;
        const t = editor.locale.t;
        const fileRepository = editor.plugins.get(FileRepository);
        const notification = editor.plugins.get(Notification);

        const fileUploadElements = this._uploadFileElements;
        model.enqueueChange({ isUndoable: false }, writer => {
            writer.setAttribute('uploadStatus', 'reading', fileUploadElements.get(loader.id));
        });

        return loader.read()
        .then(() => {
            const promise = loader.upload();
            const fileElement = fileUploadElements.get(loader.id);
            // Force re–paint in Safari. Without it, the image will display with a wrong size.
            // https://github.com/ckeditor/ckeditor5/issues/1975
            /* istanbul ignore next -- @preserve */
            if (isSafari) {
                const viewFigure = editor.editing.mapper.toViewElement(fileElement);
                const viewImg = imageUtils.findViewImgElement(viewFigure);
                editor.editing.view.once('render', () => {
                    // Early returns just to be safe. There might be some code ran
                    // in between the outer scope and this callback.
                    if (!viewImg.parent) {
                        return;
                    }
                    const domFigure = editor.editing.view.domConverter.mapViewToDom(viewImg.parent);
                    if (!domFigure) {
                        return;
                    }
                    const originalDisplay = domFigure.style.display;
                    domFigure.style.display = 'none';
                    // Make sure this line will never be removed during minification for having "no effect".
                    domFigure._ckHack = domFigure.offsetHeight;
                    domFigure.style.display = originalDisplay;
                });
            }
            if (editor.ui) {
                editor.ui.ariaLiveAnnouncer.announce(t('Uploading file'));
            }
            model.enqueueChange({ isUndoable: false }, writer => {
                writer.setAttribute('uploadStatus', 'uploading', fileElement);
            });
            return promise;
        })
        .then(data => {
            model.enqueueChange({ isUndoable: false }, writer => {
                const fileElement = fileUploadElements.get(loader.id);
                writer.setAttributes( { uploadStatus: 'complete', linkHref: data.url }, fileElement );
                if (editor.ui) {
                    editor.ui.ariaLiveAnnouncer.announce(t('File upload complete'));
                }
                this.fire('uploadComplete', { data, fileElement });
            });
            clean();
        })
        .catch(error => {
            if (editor.ui) {
                editor.ui.ariaLiveAnnouncer.announce(t('Error during file upload'));
            }
            // If status is not 'error' nor 'aborted' - throw error because it means that something else went wrong,
            // it might be generic error and it would be real pain to find what is going on.
            if (loader.status !== 'error' && loader.status !== 'aborted') {
                throw error;
            }
            // Might be 'aborted'.
            if (loader.status == 'error' && error) {
                notification.showWarning(error, {
                    title: t('Upload failed'),
                    namespace: 'upload'
                });
            }
            // Permanently remove image from insertion batch.
            model.enqueueChange({ isUndoable: false }, writer => {
                writer.remove(fileUploadElements.get(loader.id));
            });
            clean();
        });

        function clean() {
            model.enqueueChange({ isUndoable: false }, writer => {
                const fileElement = fileUploadElements.get(loader.id);
                writer.removeAttribute('uploadId', fileElement);
                writer.removeAttribute('uploadStatus', fileElement);
                fileUploadElements.delete(loader.id);
            });
            fileRepository.destroyLoader(loader);
        }
    }
}


/**
 * Returns `true` if non-empty `text/html` is included in the data transfer.
 */
export function isHtmlIncluded(dataTransfer) {
    return Array.from(dataTransfer.types).includes('text/html') && dataTransfer.getData('text/html') !== '';
}

function getFileLinksFromChangeItem(editor, item) {
    return Array.from(editor.model.createRangeOn(item))
        .filter(value => value.item.hasAttribute('linkHref'))
        .map(value => value.item);
}
